/**
 * Recuperar a conta de equipa — pedir o link, e definir a nova palavra-passe.
 *
 * São dois momentos do mesmo percurso e vivem no mesmo ficheiro porque
 * partilham tudo menos o passo: a mesma moldura, a mesma linguagem, e a mesma
 * regra de que a resposta nunca revela se a conta existe.
 *
 * Até agora o painel não tinha nada disto. Um administrador que perdesse a
 * palavra-passe era mandado para o site — outra aplicação, outra marca — ou,
 * pior, ficava trancado à espera que alguém lhe mexesse na base de dados. É a
 * conta com mais poderes da plataforma; devia ser a que se recupera melhor.
 *
 * O que **não** se faz aqui: dizer se o email existe. A resposta é sempre a
 * mesma. Numa página de gestão isso conta mais do que no site — confirmar que
 * `nome@dozy.ao` é conta de equipa é meio caminho andado para quem anda a
 * tentar entrar.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, ShieldCheck, ArrowLeft, CheckCircle2, AlertCircle, Server, Activity } from 'lucide-react';
import logo from '../assets/dozy-logo.svg';
import api from '../utils/api';

const Moldura = ({ titulo, legenda, children }) => (
  <div className="admin-login-page">
    <aside className="admin-login-visual">
      <div className="admin-login-brand"><img src={logo} alt="Dozy" /><span>Dozy <small>Admin</small></span></div>
      <div className="admin-login-message">
        <span>Recuperar acesso</span>
        <h1>A conta da equipa<br />volta a ser sua.</h1>
        <p>O link vale uma hora e só pode ser usado uma vez. Ao concluir, todas as sessões abertas são terminadas.</p>
      </div>
      <div className="admin-login-signals">
        <div><Activity /><span><strong>Pedido registado</strong><small>Fica em auditoria, com data e origem</small></span></div>
        <div><Server /><span><strong>Ambiente reservado</strong><small>Acesso exclusivo da equipa autorizada</small></span></div>
      </div>
    </aside>

    <main className="admin-login-panel">
      <div className="admin-login-form-wrap">
        <div className="admin-login-mobile-brand"><img src={logo} alt="Dozy" /><span>Dozy Admin</span></div>
        <div className="admin-login-heading">
          <div><ShieldCheck /></div>
          <span>Acesso protegido</span>
          <h2>{titulo}</h2>
          <p>{legenda}</p>
        </div>
        {children}
        <p className="admin-login-footer-note" style={{ marginTop: '18px' }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <ArrowLeft className="w-4 h-4" /> Voltar a entrar
          </Link>
        </p>
      </div>
    </main>
  </div>
);

const Aviso = ({ tom, children }) => (
  <div className={tom === 'ok' ? 'admin-login-error' : 'admin-login-error'}
    style={tom === 'ok'
      ? { background: 'rgba(22,163,74,.12)', borderColor: 'rgba(22,163,74,.35)', color: '#4ade80' }
      : undefined}>
    {tom === 'ok' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
    <span>{children}</span>
  </div>
);

/** Passo 1 — pedir o link. */
export const AdminEsqueci = () => {
  const [email, setEmail] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState('');

  const submeter = async (e) => {
    e.preventDefault();
    setBusy(true); setErro('');
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      // Sempre o mesmo ecrã, exista ou não a conta.
      setEnviado(true);
    } catch {
      // Até uma falha nossa mostra o mesmo: o contrário dizia, pela ausência
      // de mensagem, que aquele email não estava registado.
      setEnviado(true);
    } finally { setBusy(false); }
  };

  if (enviado) {
    return (
      <Moldura titulo="Verifique o email"
        legenda="Se esse endereço pertencer a uma conta de equipa, o link já vai a caminho.">
        <Aviso tom="ok">
          O link vale 1 hora e só pode ser usado uma vez. Se não chegar em alguns
          minutos, confirme o endereço ou fale com um administrador.
        </Aviso>
      </Moldura>
    );
  }

  return (
    <Moldura titulo="Recuperar a palavra-passe"
      legenda="Indique o email administrativo da sua conta.">
      {erro && <Aviso tom="erro">{erro}</Aviso>}
      <form onSubmit={submeter} className="admin-login-form">
        <div className="admin-login-field">
          <label>Email administrativo</label>
          <div>
            <Mail />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@dozy.ao" autoComplete="username" required />
          </div>
        </div>
        <button type="submit" disabled={busy || !email.trim()} className="admin-login-submit">
          {busy ? 'A enviar…' : 'Enviar link de recuperação'}
        </button>
      </form>
    </Moldura>
  );
};

/** Passo 2 — definir a nova palavra-passe a partir do link. */
export const AdminRepor = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [pw, setPw] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState('');
  const [feito, setFeito] = useState(false);

  const forte = pw.length >= 8 && /[a-zA-Z]/.test(pw) && /[0-9]/.test(pw);

  const submeter = async (e) => {
    e.preventDefault();
    if (!forte) { setErro('Pelo menos 8 caracteres, com letras e números.'); return; }
    if (pw !== confirmar) { setErro('As palavras-passe não coincidem.'); return; }
    setBusy(true); setErro('');
    try {
      // `new_password` e nao `password`: e o nome que a API espera. Enviar o
      // outro fazia-a ler `undefined`, cair na guarda dos campos em falta e
      // responder «Token e nova password sao obrigatorios» — uma mensagem que
      // fala do token e nao do campo que falta, por isso ninguem percebia.
      // A reposicao de palavra-passe do pessoal interno nunca funcionou; o
      // site do cliente sempre enviou o nome certo e por isso nunca falhou.
      await api.post('/auth/reset-password', { token, new_password: pw });
      setFeito(true);
      setTimeout(() => navigate('/', { replace: true }), 2500);
    } catch (err) {
      setErro(err.response?.data?.message || 'Link inválido ou expirado. Peça um novo.');
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <Moldura titulo="Link em falta"
        legenda="Este endereço não traz um código de reposição válido.">
        <Aviso tom="erro">Peça um link novo e abra-o directamente da mensagem que receber.</Aviso>
        <Link to="/recuperar" className="admin-login-submit"
          style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: '14px' }}>
          Pedir novo link
        </Link>
      </Moldura>
    );
  }

  if (feito) {
    return (
      <Moldura titulo="Palavra-passe alterada"
        legenda="As sessões anteriores foram terminadas.">
        <Aviso tom="ok">A levar de volta ao ecrã de entrada…</Aviso>
      </Moldura>
    );
  }

  return (
    <Moldura titulo="Nova palavra-passe"
      legenda="Escolha uma combinação exclusiva desta conta.">
      {erro && <Aviso tom="erro">{erro}</Aviso>}
      <form onSubmit={submeter} className="admin-login-form">
        <div className="admin-login-field">
          <label>Nova palavra-passe</label>
          <div>
            <Lock />
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="Mínimo 8 caracteres" autoComplete="new-password" required minLength={8} />
          </div>
        </div>
        <div className="admin-login-field">
          <label>Confirmar palavra-passe</label>
          <div>
            <Lock />
            <input type="password" value={confirmar} onChange={(e) => setConfirmar(e.target.value)}
              placeholder="Repita a palavra-passe" autoComplete="new-password" required minLength={8} />
          </div>
        </div>
        <button type="submit" disabled={busy || !forte || pw !== confirmar} className="admin-login-submit">
          {busy ? 'A guardar…' : 'Guardar nova palavra-passe'}
        </button>
      </form>
    </Moldura>
  );
};
